// ============================================================================
// Doc Order — split-aware LO reimbursement + net payout (README §8.2).
// ONE definition used by every surface (LO summary, Closing comp section,
// Banker Recon mirror, submit modal) — the four screens must always agree,
// and the $50 E&O deducts on every net-payout surface.
// ============================================================================

import { DO_CONFIG } from './config.ts';
import type { DocOrderFee } from './types.ts';

export interface ReimburseLine {
  key: string;
  name: string;
  payee: string | null;
  amount: number;
  paidBy: 'LO' | 'Lendwise' | null;
}

/**
 * One line per reimbursed portion (data.js DO_reimburseLines). A split fee
 * reimburses only its reimbursed PORTIONS; an unsplit fee reimburses in full
 * when flagged. $0 / notApplicable fees never contribute.
 */
export function reimburseLines(fees: DocOrderFee[] | null | undefined): ReimburseLine[] {
  const lines: ReimburseLine[] = [];
  (fees || []).forEach((f) => {
    if (!f || f.notApplicable || !(Number(f.amount) > 0)) return;
    const sp = f.splits && f.splits.length >= 2 ? f.splits : null;
    if (sp) {
      sp.forEach((s, i) => {
        if (s.reimburse && s.paidBy === 'LO' && (Number(s.amount) || 0) > 0) {
          lines.push({
            key: `${f.id}-${i}`,
            // Every portion is a numbered split — the parent row shows only the
            // total and carries no portion identity (Jennifer revision, bug 10/11).
            name: `${f.name} · Split ${i + 1}`,
            payee: f.payee,
            amount: Number(s.amount) || 0,
            paidBy: 'LO',
          });
        }
      });
    } else if (f.reimburse && f.paidBy === 'LO') {
      lines.push({
        key: f.id,
        name: f.name,
        payee: f.payee,
        amount: Number(f.amount) || 0,
        paidBy: 'LO',
      });
    }
  });
  return lines;
}

export function reimburseTotal(fees: DocOrderFee[] | null | undefined): number {
  return reimburseLines(fees).reduce((s, r) => s + r.amount, 0);
}

/**
 * Net LO payout = base comp + LO-paid reimbursements − $50 E&O — on EVERY surface.
 * Lendwise-paid reimbursements are company revenue in Banker Recon, never LO
 * compensation.
 * (README §8.2; matches banker recon's EO_INSURANCE → payouts.e_and_o_deduction).
 */
export function netLoPayout(baseComp: number, fees: DocOrderFee[] | null | undefined): number {
  return (Number(baseComp) || 0) + reimburseTotal(fees) - DO_CONFIG.eoInsurance;
}
